import { TotalBalance } from '@/features/Wallets/ui/TotalBalance/TotalBalance'
import { createBalancesQueryOptions, useSetMainWallet } from '@/features/Wallets/queries'
import { BalancesMapper } from '@/features/Wallets/wallet.mapper'
import { useAppWallet } from '@/shared/wallet/useAppWallet'
import { useAuthStore, useBalancesStore } from '@/store'
import { Splide, SplideSlide } from '@splidejs/react-splide'
import { useQuery } from '@tanstack/react-query'
import classnames from 'classnames'
import React, { useEffect, useRef, useState } from 'react'
import styles from './TotalBalanceSlider.module.scss'
import '@splidejs/react-splide/css'

export function TotalBalanceSlider() {
  const sliderRef = useRef(null)
  const selectedWalletType = useAuthStore((state) => state.selectedWalletType)
  const wallet = useAppWallet(selectedWalletType)

  const selectedWallet = useBalancesStore((state) => state.selectedWallet)
  const setSelectedWallet = useBalancesStore((state) => state.setSelectedWallet)

  const { data: balances } = useQuery(createBalancesQueryOptions())
  const { mutate: setMainWallet } = useSetMainWallet()

  const [order, setOrder] = useState<string[]>([])

  let orderedBalances: typeof balances
  if (balances) {
    const nextOrder = BalancesMapper.reconcileWalletOrder(order, balances)
    if (nextOrder.length !== order.length || nextOrder.some((address, i) => address !== order[i])) {
      setOrder(nextOrder)
    }
    const balanceByAddress = new Map(balances.map((balance) => [balance.address, balance]))
    orderedBalances = nextOrder.map((address) => balanceByAddress.get(address))
  }

  useEffect(() => {
    if (selectedWallet && orderedBalances) {
      const index = orderedBalances.findIndex((balance) => balance.address === selectedWallet.address)
      if (sliderRef.current && index >= 0) {
        sliderRef.current.go(index)
      }
    }
  }, [selectedWallet, orderedBalances])

  const latestOnActive = useRef<(index: number) => void>(null)

  useEffect(() => {
    latestOnActive.current = (index) => {
      const balance = orderedBalances?.[index]
      if (balance) {
        setSelectedWallet(balance.address, balance.name)
        if (!balance.isMain) {
          setMainWallet(balance.address)
        }
      }
    }
  })

  if (!orderedBalances || orderedBalances.length === 0) {
    return <TotalBalance walletName="Wallet" wallet={wallet.walletName} publicKey={undefined} isPublicKey />
  }

  return (
    <div className={classnames(styles.root, orderedBalances.length > 1 && styles.rootMargin)}>
      <Splide
        ref={sliderRef}
        aria-label="Wallets list"
        onActive={({ index }) => latestOnActive.current?.(index)}
        options={{
          arrows: false,
          pagination: true,
          waitForTransition: true,
          perPage: 1,
          perMove: 1,
          speed: 500,
          direction: 'ltr',
          gap: 8,
          padding: 16,
        }}
      >
        {orderedBalances.map((balance) => (
          <SplideSlide key={balance.address}>
            <TotalBalance
              walletName={balance.name || 'Account'}
              wallet={wallet.walletName}
              publicKey={balance.address}
              isPublicKey
            />
          </SplideSlide>
        ))}
      </Splide>
    </div>
  )
}
